import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { Box, Typography } from '@mui/material'
import { useFundingSummaryById } from '@src/data/api/credit-api/credit-api'
import { useTranslation } from 'react-i18next'
import PageSpinner from '../../components/PageSpinner'
import ClientInformation from './components/ClientInformation'
import FundingSummaryButtonGroup from './components/FundingSummaryButtonGroup'
import IFinanceMerchantInformation from './components/IFinanceMerchantInformation'
import IcebergMerchantInformation from './components/IcebergMerchantInformation'
import LoanInformation from './components/LoanInformation'

interface Props {
  id: string
  financingProgramId: EFinancingProgram
}
const FundingSummary = ({ id, financingProgramId }: Props) => {
  const { t } = useTranslation()

  const [fundingSummary, isLoadingSummary] = useFundingSummaryById(id, financingProgramId)

  if (isLoadingSummary) return <PageSpinner isLoading withBackdrop />
  if (!fundingSummary) return null

  return (
    <Box
      sx={{
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '90%',
        maxWidth: '1100px',
        '@media print': {
          width: '100%',
        },
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 0.5, textAlign: 'center', width: '70%' }}>
        {t('fundingSummary.formTitle')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', width: '70%', '@media print': { mb: 1 } }}>
        {t('fundingSummary.guaranteeOfPayment')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 4 }}>
        {/* Main content */}
        <Box sx={{ flex: '1 1 70%' }}>
          <LoanInformation fundingSummary={fundingSummary} />

          {fundingSummary.financingCompanyId === EFinancingCompany.Iceberg ? (
            <IcebergMerchantInformation fundingSummary={fundingSummary} />
          ) : (
            <IFinanceMerchantInformation fundingSummary={fundingSummary} />
          )}

          <ClientInformation fundingSummary={fundingSummary} />

          <FundingSummaryButtonGroup />
        </Box>

        {/* Sidebar */}
        <Box
          sx={{
            flex: '0 0 28%',
            paddingTop: '24px',
            borderLeft: '1px solid',
            borderColor: 'divider',
            paddingLeft: 3,
            '@media print': { borderColor: '#999' },
          }}
        >
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('fundingSummary.changesRequiredNote')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('fundingSummary.contactUsIfBankDetailsChanged')}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            {t('fundingSummary.notes')}
          </Typography>
          {fundingSummary?.fcfNote && (<Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {fundingSummary.fcfNote}
          </Typography>)}
        </Box>
      </Box>
    </Box>
  )
}

export default FundingSummary
